<template>
  <f7-card ref="cardEl" class="product-card" @click="handleView">
    <div class="product-img">
      <img v-if="mainImage" :src="mainImage" @error="mainImage = ''" />
      <f7-icon v-else :f7="kindIcon" size="26" />
      <span v-if="isSubscription && summary.count > 1" class="offers-badge">
        {{ $t('marketplace.offersCount', { count: summary.count }) }}
      </span>
      <span v-if="product.groupBuyEnabled && groupBuyCampaign" class="group-badge">
        {{ $t('marketplace.groupBuy') }} · {{ groupBuyCampaign.currentParticipants || 0 }}/{{ groupBuyCampaign.minimumParticipants }}
      </span>
      <span v-if="soldOut" class="soldout-badge">{{ $t('marketplace.soldOut') }}</span>
    </div>
    <f7-card-content class="product-body">
      <div class="kind-row" v-if="kind !== 'PHYSICAL' || product.negotiable">
        <span class="kind-chip" :class="kind.toLowerCase()">
          <f7-icon :f7="kindIcon" size="9" /> {{ kindLabel }}
        </span>
        <span v-if="product.negotiable" class="negotiable-hint">{{ $t('marketplace.negotiable') }}</span>
      </div>
      <div class="product-name">{{ product.name }}</div>

      <!-- Subscription / multi-offer: "from X /period · n offers" -->
      <template v-if="isSubscription && summary.count > 1">
        <div class="price-row">
          <span class="price-from">{{ $t('marketplace.from') }}</span>
          <span class="price-main">{{ formatPrice(summary.min, summary.billingPeriod) }}</span>
        </div>
        <div class="plans-hint">
          <f7-icon f7="square_grid_2x2_fill" size="10" />
          {{ summary.count }} {{ $t('marketplace.availablePlans') }}
        </div>
      </template>

      <!-- Single offer (any kind) -->
      <template v-else>
        <div class="price-row">
          <span class="price-main">{{ formatPrice(primaryOffer.unitPrice, primaryOffer.billingPeriod) }}</span>
          <span
            v-if="memberDiscount"
            class="price-orig"
          >
            {{ formatPrice(memberDiscount, 'XOF', false) }}
          </span>
        </div>
        <div v-if="memberOffer" class="member-hint">
          <f7-icon f7="lock_fill" size="10" /> {{ formatPrice(memberOffer.unitPrice, memberOffer.billingPeriod) }} {{ $t('marketplace.members') }}
        </div>
      </template>

      <f7-button small fill raised round class="add-btn" :color="soldOut ? 'gray' : ''" @click.stop="handleAdd">
        {{ ctaLabel }}
      </f7-button>
    </f7-card-content>
  </f7-card>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useEngagementTracker } from '../../js/composables/useEngagementTracker'
import {
  productKind,
  normalizeOffers,
  summarizeOffers,
  totalStock,
  formatOfferPrice,
} from '../../js/composables/useProductOffer.js'
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({
  product: { type: Object, required: true },
  media: { type: Array, default: () => [] },
  variants: { type: Array, default: () => [] },
  priceRules: { type: Array, default: () => [] },
  groupBuyCampaign: { type: Object, default: null },
  placementType: { type: String, default: 'MARKETPLACE' },
  placementId: { type: String, default: null },
})
const emit = defineEmits(['add', 'view', 'negotiate', 'buy'])
const cardEl = ref(null)
const tracker = useEngagementTracker()
let stopObserve

function base() {
  return {
    contentType: 'PRODUCT',
    contentId: props.product.id,
    promotionId: props.product.promotionId || null,
    placementType: props.placementType,
    placementId: props.placementId,
  }
}

onMounted(() => {
  stopObserve = tracker.observeViewable(cardEl.value?.$el || cardEl.value, base())
})
onBeforeUnmount(() => stopObserve?.())

function handleView() {
  tracker.trackClick(base())
  emit('view', props.product)
}

function handleAdd() {
  if (soldOut.value) return
  tracker.track(isPhysical ? 'ADD_TO_CART' : 'BUY_INTENT', base())
  if (isPhysical) {
    emit(props.product.negotiable ? 'negotiate' : 'add', props.product)
  } else {
    // Digital/service/subscription: open detail to pick an offer first.
    emit('view', props.product)
    emit('buy', props.product)
  }
}

// ── Kind & offers ────────────────────────────────────────────────
const kind = computed(() => productKind(props.product))
const isPhysical = computed(() => kind.value === 'PHYSICAL')
const isSubscription = computed(() => kind.value === 'SUBSCRIPTION')

const KIND_META = {
  PHYSICAL: { icon: 'bag_fill', labelKey: 'marketplace.kindPhysical' },
  DIGITAL: { icon: 'cloud_fill', labelKey: 'marketplace.kindDigital' },
  SERVICE: { icon: 'wrench_fill', labelKey: 'marketplace.kindService' },
  SUBSCRIPTION: { icon: 'arrow_2_circlepath', labelKey: 'marketplace.kindSubscription' },
}
const kindIcon = computed(() => KIND_META[kind.value]?.icon || 'bag_fill')
const kindLabel = computed(() => t(KIND_META[kind.value]?.labelKey || 'marketplace.kindPhysical'))

const offers = computed(() => normalizeOffers(props.product, props.variants, props.priceRules))
const summary = computed(() => summarizeOffers(offers.value))
const primaryOffer = computed(() => offers.value[0] || { unitPrice: 0, billingPeriod: 'ONE_TIME' })
const memberOffer = computed(() => offers.value.find((o) => o.raw?.type === 'MEMBER') || null)
const memberDiscount = computed(() => {
  if (!memberOffer.value) return null
  const base = offers.value.find((o) => o !== memberOffer.value)
  return base && base.unitPrice > memberOffer.unitPrice ? base.unitPrice : null
})

const stock = computed(() => totalStock(offers.value))
const soldOut = computed(() => isPhysical.value && stock.value === 0)

const ctaLabel = computed(() => {
  if (soldOut.value) return t('marketplace.soldOut')
  if (isPhysical.value) {
    return props.product.negotiable ? t('marketplace.negotiate') : t('marketplace.addToCart')
  }
  if (isSubscription.value) return t('marketplace.subscribe')
  if (kind.value === 'SERVICE') return t('marketplace.bookNow')
  return t('shopProduct.buyNow')
})

const mainImage = computed(() => {
  const main = props.media.find((m) => m.mainImage && m.mediaType === 'IMAGE')
  if (main) return main.url
  const first = props.media.find((m) => m.mediaType === 'IMAGE')
  return first?.url || ''
})

function formatPrice(amount, billingPeriod = 'ONE_TIME', suffix = true) {
  return formatOfferPrice(amount, props.product.currency || 'XOF', suffix ? billingPeriod : 'ONE_TIME')
}
</script>

<style scoped>
.product-card { margin: 0; cursor: pointer; }
.product-img {
  aspect-ratio: 1; background: var(--f7-list-bg-color, #f5f5f5);
  display: flex; align-items: center; justify-content: center; position: relative;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.product-img img { width: 100%; height: 100%; object-fit: cover; }
.group-badge,
.offers-badge,
.soldout-badge {
  position: absolute;
  color: #fff; font-size: 9px;
  padding: 2px 7px; border-radius: 5px;
}
.group-badge { top: 6px; left: 6px; background: rgba(0,0,0,.6); }
.offers-badge { top: 6px; right: 6px; background: rgba(12,68,124,.85); }
.soldout-badge { top: 6px; left: 6px; background: rgba(200,40,40,.85); text-transform: uppercase; letter-spacing: .04em; }
.product-body { padding: 8px 10px; }
.kind-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 3px; gap: 4px; }
.kind-chip {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 8.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em;
  padding: 2px 6px; border-radius: 4px;
  background: var(--f7-list-bg-color, #f2f2f7); color: var(--f7-on-surface-variant, #666);
}
.kind-chip.digital { background: #e8f0fb; color: #0c447c; }
.kind-chip.service { background: #fdf3e4; color: #8a5a00; }
.kind-chip.subscription { background: #eaf3de; color: #27500a; }
.negotiable-hint { font-size: 9px; color: var(--f7-on-surface-variant, #8e8e93); }
.product-name { font-size: 12px; font-weight: 500; margin-bottom: 3px; }
.price-row { display: flex; align-items: baseline; gap: 5px; flex-wrap: wrap; }
.price-from { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); }
.price-main { font-size: 14px; font-weight: 500; color: var(--f7-theme-color); }
.price-orig { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); text-decoration: line-through; }
.plans-hint { font-size: 10px; color: #27500a; margin-top: 2px; display: flex; align-items: center; gap: 3px; }
.member-hint { font-size: 10px; color: var(--f7-theme-color); margin-top: 2px; display: flex; align-items: center; gap: 3px; }
.add-btn { width: 100%; margin-top: 6px; }
</style>
